import { QueryDocumentSnapshot, serverTimestamp, SnapshotOptions } from "firebase/firestore";

export type SupportedLanguage = "de" | "en";

/**
 * Per-Gemeinde channel release flags. The superadmin decides which notification
 * channels a Gemeinde may use at all; the Gemeinde can then enable/disable the
 * released channels in its own notification settings. A channel only sends when
 * it is BOTH released here AND enabled in the Gemeinde's NotificationSettings.
 */
export interface ChannelRelease {
    emailReleased: boolean;
    whatsappReleased: boolean;
    pushReleased: boolean;
}

export const DEFAULT_CHANNEL_RELEASE: ChannelRelease = {
    emailReleased: false,
    whatsappReleased: false,
    pushReleased: false,
};

/**
 * A tenant (Standort/Gemeinde). The document holds only non-secret fields, so it
 * is safe for Gemeinde members to read. The recording-page password is NOT stored
 * here – it lives in a functions-only secrets doc; this document only records
 * whether one has been set (`hasRecordingPassword`).
 */
export default class Gemeinde {
    id: string;            // Firestore document id (not persisted as a field)
    name: string;
    slug: string;          // unique, lowercase, URL-safe – used for public pages
    address: string;
    contactEmail: string;
    contactPhone: string;
    logoUrl: string;
    brandColor: string;
    senderName: string;       // shown as sender in reminders (credentials stay central)
    senderSignature: string;  // appended to email/WhatsApp reminders
    defaultLanguage: SupportedLanguage;
    holidayRegion: string;    // German Bundesland code for regional holidays, e.g. "NW"
    choirSheetUrl: string;    // optional per-Gemeinde Google-Sheet source for choir sync
    channels: ChannelRelease;
    hasRecordingPassword: boolean;
    // Recording location (GOD-46). undefined = never set (legacy SE/Sennestadt applies).
    recordingLocationCode?: string;
    recordingLocationLabel?: string;

    constructor(
        id: string,
        name: string,
        slug: string,
        address: string = "",
        contactEmail: string = "",
        contactPhone: string = "",
        logoUrl: string = "",
        brandColor: string = "",
        senderName: string = "",
        senderSignature: string = "",
        defaultLanguage: SupportedLanguage = "de",
        holidayRegion: string = "",
        choirSheetUrl: string = "",
        channels: ChannelRelease = { ...DEFAULT_CHANNEL_RELEASE },
        hasRecordingPassword: boolean = false
    ) {
        this.id = id;
        this.name = name;
        this.slug = slug;
        this.address = address;
        this.contactEmail = contactEmail;
        this.contactPhone = contactPhone;
        this.logoUrl = logoUrl;
        this.brandColor = brandColor;
        this.senderName = senderName;
        this.senderSignature = senderSignature;
        this.defaultLanguage = defaultLanguage;
        this.holidayRegion = holidayRegion;
        this.choirSheetUrl = choirSheetUrl;
        this.channels = channels;
        this.hasRecordingPassword = hasRecordingPassword;
    }
}

export const gemeindeConverter = {
    toFirestore: (gemeinde: Gemeinde) => {
        return {
            name: gemeinde.name,
            slug: gemeinde.slug,
            address: gemeinde.address ?? "",
            contactEmail: gemeinde.contactEmail ?? "",
            contactPhone: gemeinde.contactPhone ?? "",
            logoUrl: gemeinde.logoUrl ?? "",
            brandColor: gemeinde.brandColor ?? "",
            senderName: gemeinde.senderName ?? "",
            senderSignature: gemeinde.senderSignature ?? "",
            defaultLanguage: gemeinde.defaultLanguage ?? "de",
            holidayRegion: gemeinde.holidayRegion ?? "",
            choirSheetUrl: gemeinde.choirSheetUrl ?? "",
            channels: gemeinde.channels ?? { ...DEFAULT_CHANNEL_RELEASE },
            hasRecordingPassword: gemeinde.hasRecordingPassword ?? false,
            updatedAt: serverTimestamp(),
        };
    },

    fromFirestore: (snapshot: QueryDocumentSnapshot, options: SnapshotOptions) => {
        const data = snapshot.data(options);
        const channels: ChannelRelease = {
            emailReleased: data.channels?.emailReleased ?? false,
            whatsappReleased: data.channels?.whatsappReleased ?? false,
            pushReleased: data.channels?.pushReleased ?? false,
        };
        const gemeinde = new Gemeinde(
            snapshot.id,
            data.name ?? "",
            data.slug ?? "",
            data.address ?? "",
            data.contactEmail ?? "",
            data.contactPhone ?? "",
            data.logoUrl ?? "",
            data.brandColor ?? "",
            data.senderName ?? "",
            data.senderSignature ?? "",
            (data.defaultLanguage as SupportedLanguage) ?? "de",
            data.holidayRegion ?? "",
            data.choirSheetUrl ?? "",
            channels,
            data.hasRecordingPassword ?? false
        );
        if (typeof data.recordingLocationCode === "string") {
            gemeinde.recordingLocationCode = data.recordingLocationCode;
            gemeinde.recordingLocationLabel = data.recordingLocationLabel ?? "";
        }
        return gemeinde;
    },
};
